import { FC, useContext, useEffect, useState } from 'react';
import { Redirect, Route, RouteProps, useHistory } from 'react-router-dom';
import { UserContext } from '../../context';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useFlags, useAllowedNavRoutes } from '../../hooks';
import ReactGA from 'react-ga4';
import { useSnackbar } from 'notistack';
import { Loader } from '../loader';
import { Page } from './Page';
import { useMsal } from '@azure/msal-react';
import { finishPendingRequests, getCurrentNavigationArea } from '../../helpers/axios';
import { logWarning } from '../../services';

interface IPrivateRoute extends RouteProps {
  featureFlag?: string;
  legacyUrl?: string;
  roles?: string[];
  combinationRoles?: string[];
  pageIsInternalOnly?: boolean;
  pageIsMemberOnly?: boolean;
  globalAdminOverride?: boolean; // if true, will allow GlobalAdmin to access the page even if roles are not met
  children: React.ReactNode;
}

export const PrivateRoute: FC<IPrivateRoute> = ({
  featureFlag,
  legacyUrl,
  roles,
  combinationRoles,
  pageIsInternalOnly = false,
  pageIsMemberOnly = false,
  globalAdminOverride = false,
  children,
  ...props
}) => {
  const { user, isLoading, isGlobalAdmin } = useContext(UserContext);
  const featureFlags = useFlags();
  const history = useHistory();
  const ldContext = useLDClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isRedirecting, setRedirecting] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const { availableLinks, isNavLoading } = useAllowedNavRoutes(featureFlags, user);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);

  const trackPage = (page: string) => {
    ReactGA.set({ page });
    ReactGA.send({ hitType: 'pageview', page });
  };

  const onlyInternalAccess = pageIsInternalOnly && user?.externalUser;

  useEffect(() => {
    // Reset the redirect flag except at / to prevent duplicate snackbar.
    if (props.location.pathname !== '/') {
      setHasRedirected(false);
    }
    trackPage(props.location.pathname);
    // any pending requests, cancel them so we don't get any possible BE errors on the wrong pages
    return () => {
      finishPendingRequests('RouteChange');
    };
  }, [props.location.pathname]);

  useEffect(() => {
    if (user) {
      if (featureFlags?.authorizeUserNavigationAccess && isNavLoading) {
        return;
      }

      if (!hasRedirected && featureFlags?.authorizeUserNavigationAccess) {
        let currentNavArea = getCurrentNavigationArea(window?.location?.pathname);
        // regardless if the user is editing a custom page, check only the top level custom pages available navigation
        // only on the admin edit, otherwise take the currentNav Area, so don't check Custom Pages/{slug
        if (currentNavArea.includes('Admin/Manage Content/Custom Pages')) {
          currentNavArea = 'Admin/Manage Content/Custom Pages';
        }
        if (!availableLinks.has(currentNavArea)) {
          logWarning(
            {
              currentNavArea,
              availableLinks,
              user
            },
            'Not the correct permissions'
          );
          enqueueSnackbar('Not the correct permissions, redirecting...', {
            variant: 'info'
          });
          setHasRedirected(true);
          history.push(`/`);
          setIsCheckingPermissions(false);
          return;
        }
      }
    }

    let mounted = true;
    const checkFF = () => {
      //@ts-ignore
      const context = ldContext?.getContext();
      // need to check for logged in users context before we evaluate the current FF
      if (context && !context.anonymous) {
        const hasFFTurnedOff =
          !isLoading && featureFlags && featureFlag && !featureFlags[featureFlag];

        var hasNotTheCorrectRoles = false;

        // if combinationRoles are passed in, check if the user has all of the roles
        if (combinationRoles?.length > 0) {
          hasNotTheCorrectRoles = !combinationRoles.every(r => user?.userRoles?.includes(r));
        } else {
          // check the logged in users's roles and see if they are valid
          hasNotTheCorrectRoles =
            !isLoading &&
            roles?.length > 0 &&
            !user?.userRoles?.some(r => roles.includes(r)) &&
            !roles.includes(user?.placeOrder?.placeOrderRole?.name);
        }

        // if the user is denied access from above but override is true check if they are a global admin
        if (globalAdminOverride && hasNotTheCorrectRoles) {
          hasNotTheCorrectRoles = !isGlobalAdmin;
        }

        // feature flag is turned off and there is no legacy url getting passed in, redirect to home page
        // need to check the user is loaded and we have featureFlags loaded
        if (hasFFTurnedOff && !legacyUrl) {
          enqueueSnackbar(`Redirecting...`, {
            variant: 'info'
          });
          setIsCheckingPermissions(false);
          history.push('/');
        }
        // feature flag is turned off and there is a legacyUrl passed in, redirect to legacy
        else if (hasFFTurnedOff && legacyUrl) {
          setRedirecting(true);
          window.location.href = legacyUrl;
        } else if (
          !featureFlags?.authorizeUserNavigationAccess &&
          hasNotTheCorrectRoles &&
          mounted
        ) {
          enqueueSnackbar(`Not the right roles, redirecting...`, {
            variant: 'info'
          });
          history.push(`/`);
          setIsCheckingPermissions(false);
        } else {
          setIsCheckingPermissions(false);
        }
      }
    };

    if (onlyInternalAccess) {
      enqueueSnackbar(`Not an internal user, redirecting...`, {
        variant: 'info'
      });
      history.push(`/`);
      setIsCheckingPermissions(false);
    }

    if (user && pageIsMemberOnly && !user?.isMember) {
      enqueueSnackbar(`Not a member, redirecting...`, {
        variant: 'info'
      });
      history.push(`/`);
      setIsCheckingPermissions(false);
    }

    if (mounted) {
      checkFF();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    featureFlag,
    legacyUrl,
    featureFlags,
    history,
    enqueueSnackbar,
    roles,
    user,
    ldContext,
    availableLinks,
    props.location.pathname
  ]);

  const { accounts } = useMsal();

  const account = accounts[0] ?? null;

  if (isRedirecting) {
    return (
      <Page title="Redirecting...">
        <Loader type="fullscreen" position="centered" title="Redirecting..." />
      </Page>
    );
  }

  if (!user && !isLoading && !account) {
    return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
  }

  if (isLoading || isNavLoading || isCheckingPermissions) {
    return <Loader type="fullscreen" position="centered" />;
  }

  return (
    <Route
      {...props}
      exact
      render={location => {
        return user && !isLoading && !isCheckingPermissions && !isNavLoading ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: {
                from: `${location?.location?.pathname ?? '/'}${location?.location?.search ?? ''}`
              }
            }}
          />
        );
      }}
    />
  );
};
